import axios from 'axios';
import { AddImageCommand, FileDataAsString, IdResponse, ImageResponse, ImportCommand, ImportResult, SetIsActiveCommand, UpdateNameCommand, UpdateProgramPageImageResponse } from './models/common';
import { ChangeDivisionCommand, CopyMemberDataCommand, CopyMemberResult, CreateOrgSeasonCommand, CreateSponsorCommand, OrgSeason } from './models/orgSeason';
import { CreateTeamCommand, TeamCardSummary } from './models/team';
import { GameCommand } from './models/game';
import { CustomProgramUpdateCommand, ProgramOptions } from './models/program';

export async function addOrgSeasonApi(bearerToken: string, createOrgSeason: CreateOrgSeasonCommand): Promise<void> {
  const baseUrl = process.env.REACT_APP_API_URL;
  const endpoint = `/api/v1/orgSeason`;
  await axios.post(baseUrl + endpoint, createOrgSeason, { timeout: 60000, headers: { 'Authorization': `Bearer ${bearerToken}` } });
}

export async function importMembersApi(bearerToken: string, orgSeasonGuid: string, importData: ImportCommand): Promise<ImportResult> {
  const baseUrl = process.env.REACT_APP_API_URL;
  const endpoint = `/api/v1/orgSeason/${orgSeasonGuid}/import`;
  const response = await axios.post(baseUrl + endpoint, importData, { timeout: 60000, headers: { 'Authorization': `Bearer ${bearerToken}` } });
  return response.data as ImportResult;
}

export async function exportMembersApi(bearerToken: string, orgSeasonGuid: string): Promise<FileDataAsString> {
  const baseUrl = process.env.REACT_APP_API_URL;
  const endpoint = `/api/v1/orgSeason/${orgSeasonGuid}/export`;
  const response = await axios.get(baseUrl + endpoint, { timeout: 60000, headers: { 'Authorization': `Bearer ${bearerToken}` } });
  return response.data as FileDataAsString;
}

export async function getSampleImportFileApi(bearerToken: string): Promise<FileDataAsString> {
  const baseUrl = process.env.REACT_APP_API_URL;
  const endpoint = `/api/v1/document/sample-import-file`;
  const response = await axios.get(baseUrl + endpoint, { timeout: 60000, headers: { 'Authorization': `Bearer ${bearerToken}` } });
  return response.data as FileDataAsString;
}

export async function updateOrgSeasonLogoApi(bearerToken: string, orgSeasonGuid: string, imageData: AddImageCommand): Promise<void> {
  const baseUrl = process.env.REACT_APP_API_URL;
  const endpoint = `/api/v1/orgSeason/${orgSeasonGuid}/logo`;
  await axios.put(baseUrl + endpoint, imageData, { timeout: 60000, headers: { 'Authorization': `Bearer ${bearerToken}` } });
}

export async function updateOrgSeasonNameApi(bearerToken: string, orgSeasonGuid: string, nameCommand: UpdateNameCommand): Promise<void> {
  const baseUrl = process.env.REACT_APP_API_URL;
  const endpoint = `/api/v1/orgSeason/${orgSeasonGuid}/name`;
  await axios.put(baseUrl + endpoint, nameCommand, { timeout: 60000, headers: { 'Authorization': `Bearer ${bearerToken}` } });
}

export async function getSingleOrgSeasonApi(bearerToken: string, orgSeasonGuid: string): Promise<OrgSeason> {
  const baseUrl = process.env.REACT_APP_API_URL;
  const endpoint = `/api/v1/orgSeason/${orgSeasonGuid}`;

  const headers: Record<string, string> = {};

  if (bearerToken) {
    headers['Authorization'] = `Bearer ${bearerToken}`;
  }

  const response = await axios.get(baseUrl + endpoint, { timeout: 60000, headers });
  return response.data as OrgSeason;
}

export async function deleteSingleOrgSeasonApi(bearerToken: string, orgSeasonGuid: string): Promise<OrgSeason> {
  const baseUrl = process.env.REACT_APP_API_URL;
  const endpoint = `/api/v1/orgSeason/${orgSeasonGuid}`;
  const response = await axios.delete(baseUrl + endpoint, { timeout: 60000, headers: { 'Authorization': `Bearer ${bearerToken}` } });
  return response.data as OrgSeason;
}

export async function addSponsorApi(bearerToken: string, orgSeasonGuid: string, createSponsor: CreateSponsorCommand): Promise<void> {
  const baseUrl = process.env.REACT_APP_API_URL;
  const endpoint = `/api/v1/orgSeason/${orgSeasonGuid}/sponsor`;
  await axios.post(baseUrl + endpoint, createSponsor, { timeout: 60000, headers: { 'Authorization': `Bearer ${bearerToken}` } });
}

export async function setSponsorIsActiveApi(bearerToken: string, orgSeasonGuid: string, sponsorId: string, isActiveCommand: SetIsActiveCommand): Promise<void> {
  const baseUrl = process.env.REACT_APP_API_URL;
  const endpoint = `/api/v1/orgSeason/${orgSeasonGuid}/sponsor/${sponsorId}/isActive`;
  await axios.put(baseUrl + endpoint, isActiveCommand, { timeout: 60000, headers: { 'Authorization': `Bearer ${bearerToken}` } });
}

export async function updateSponsorApi(bearerToken: string, orgSeasonGuid: string, sponsorId: string, createSponsor: CreateSponsorCommand): Promise<void> {
  const baseUrl = process.env.REACT_APP_API_URL;
  const endpoint = `/api/v1/orgSeason/${orgSeasonGuid}/sponsor/${sponsorId}`;
  await axios.put(baseUrl + endpoint, createSponsor, { timeout: 60000, headers: { 'Authorization': `Bearer ${bearerToken}` } });
}

export async function updateSponsorLogoApi(bearerToken: string, orgSeasonGuid: string, sponsorId: string, imageData: AddImageCommand): Promise<void> {
  const baseUrl = process.env.REACT_APP_API_URL;
  const endpoint = `/api/v1/orgSeason/${orgSeasonGuid}/sponsor/${sponsorId}/logo`;
  await axios.put(baseUrl + endpoint, imageData, { timeout: 60000, headers: { 'Authorization': `Bearer ${bearerToken}` } });
}

export async function updateSponsorAdImageApi(bearerToken: string, orgSeasonGuid: string, sponsorId: string, imageData: AddImageCommand): Promise<void> {
  const baseUrl = process.env.REACT_APP_API_URL;
  const endpoint = `/api/v1/orgSeason/${orgSeasonGuid}/sponsor/${sponsorId}/adImage`;
  await axios.put(baseUrl + endpoint, imageData, { timeout: 60000, headers: { 'Authorization': `Bearer ${bearerToken}` } });
}

export async function deleteSponsorApi(bearerToken: string, orgSeasonGuid: string, sponsorId: string): Promise<void> {
  const baseUrl = process.env.REACT_APP_API_URL;
  const endpoint = `/api/v1/orgSeason/${orgSeasonGuid}/sponsor/${sponsorId}`;
  await axios.delete(baseUrl + endpoint, { timeout: 60000, headers: { 'Authorization': `Bearer ${bearerToken}` } });
}

export async function changeDivisionApi(bearerToken: string, orgSeasonGuid: string, divisionCommand: ChangeDivisionCommand): Promise<void> {
  const baseUrl = process.env.REACT_APP_API_URL;
  const endpoint = `/api/v1/orgSeason/${orgSeasonGuid}/division`;
  await axios.post(baseUrl + endpoint, divisionCommand, { timeout: 60000, headers: { 'Authorization': `Bearer ${bearerToken}` } });
}

//manage Team data

export async function addTeamApi(bearerToken: string, orgSeasonGuid: string, createTeam: CreateTeamCommand): Promise<void> {
  const baseUrl = process.env.REACT_APP_API_URL;
  const endpoint = `/api/v1/orgSeason/${orgSeasonGuid}/team`;
  await axios.post(baseUrl + endpoint, createTeam, { timeout: 60000, headers: { 'Authorization': `Bearer ${bearerToken}` } });
}

export async function getTeamApi(bearerToken: string, orgSeasonGuid: string, teamGuid: string): Promise<void> {
  const baseUrl = process.env.REACT_APP_API_URL;
  const endpoint = `/api/v1/orgSeason/${orgSeasonGuid}/team/${teamGuid}`;

  const headers: Record<string, string> = {};

  if (bearerToken) {
    headers['Authorization'] = `Bearer ${bearerToken}`;
  }

  await axios.get(baseUrl + endpoint, { timeout: 60000, headers });
}

export async function updateTeamNameApi(bearerToken: string, orgSeasonGuid: string, teamGuid: string, nameCommand: UpdateNameCommand): Promise<void> {
  const baseUrl = process.env.REACT_APP_API_URL;
  const endpoint = `/api/v1/orgSeason/${orgSeasonGuid}/team/${teamGuid}/name`;
  await axios.put(baseUrl + endpoint, nameCommand, { timeout: 60000, headers: { 'Authorization': `Bearer ${bearerToken}` } });
}

export async function updateTeamLogoApi(bearerToken: string, orgSeasonGuid: string, teamGuid: string, imageData: AddImageCommand): Promise<void> {
  const baseUrl = process.env.REACT_APP_API_URL;
  const endpoint = `/api/v1/orgSeason/${orgSeasonGuid}/team/${teamGuid}/logo`;
  await axios.put(baseUrl + endpoint, imageData, { timeout: 60000, headers: { 'Authorization': `Bearer ${bearerToken}` } });
}

export async function updateTeamCardEditStatusApi(bearerToken: string, orgSeasonGuid: string, teamGuid: string, command: TeamCardSummary): Promise<void> {
  const baseUrl = process.env.REACT_APP_API_URL;
  const endpoint = `/api/v1/orgSeason/${orgSeasonGuid}/team/${teamGuid}/card-edit-status`;
  await axios.put(baseUrl + endpoint, command, { timeout: 60000, headers: { 'Authorization': `Bearer ${bearerToken}` } });
}

export async function deleteTeamApi(bearerToken: string, orgSeasonGuid: string, teamGuid: string): Promise<void> {
  const baseUrl = process.env.REACT_APP_API_URL;
  const endpoint = `/api/v1/orgSeason/${orgSeasonGuid}/team/${teamGuid}`;
  await axios.delete(baseUrl + endpoint, { timeout: 60000, headers: { 'Authorization': `Bearer ${bearerToken}` } });
}

//Team menu handlers

export async function generateRosterApi(bearerToken: string, teamGuid: string): Promise<FileDataAsString> {
  const baseUrl = process.env.REACT_APP_API_URL;
  const endpoint = `/api/v1/team/${teamGuid}/roster/generate`;
  const response = await axios.get(baseUrl + endpoint, { timeout: 60000, headers: { 'Authorization': `Bearer ${bearerToken}` } });
  return response.data as FileDataAsString;
}

export async function generateProgramApi(bearerToken: string, teamGuid: string): Promise<FileDataAsString> {
  const baseUrl = process.env.REACT_APP_API_URL;
  const endpoint = `/api/v1/team/${teamGuid}/program/generate`;
  const response = await axios.get(baseUrl + endpoint, { timeout: 60000, headers: { 'Authorization': `Bearer ${bearerToken}` } });
  return response.data as FileDataAsString;
}

//custom program API

export async function createCustomProgramApi(bearerToken: string, teamGuid: string, command:UpdateNameCommand): Promise<IdResponse> {
  const baseUrl = process.env.REACT_APP_API_URL;
  const endpoint = `/api/v1/team/${teamGuid}/program/custom/create`;
  const response = await axios.post(baseUrl + endpoint, command,{ timeout: 60000, headers: { 'Authorization': `Bearer ${bearerToken}` } });
  return response.data as IdResponse;
}

export async function updateCustomProgramApi(bearerToken: string, teamGuid: string, customProgramGuid: string, programOptions:CustomProgramUpdateCommand): Promise<IdResponse> {
  const baseUrl = process.env.REACT_APP_API_URL;
  const endpoint = `/api/v1/team/${teamGuid}/program/custom/${customProgramGuid}/update`;
  const response = await axios.put(baseUrl + endpoint, programOptions,{ timeout: 60000, headers: { 'Authorization': `Bearer ${bearerToken}` } });
  return response.data as IdResponse;
}

export async function updateCustomProgramOpponentLogoApi(bearerToken: string, teamGuid: string, customProgramGuid: string, image:ImageResponse): Promise<IdResponse> {
  const baseUrl = process.env.REACT_APP_API_URL;
  const endpoint = `/api/v1/team/${teamGuid}/program/custom/${customProgramGuid}/opponent-logo`;
  const response = await axios.put(baseUrl + endpoint, image,{ timeout: 60000, headers: { 'Authorization': `Bearer ${bearerToken}` } });
  return response.data as IdResponse;
}

export async function updateCustomProgramSpecialSheetApi(bearerToken: string, teamGuid: string, customProgramGuid: string, imagePageUpdate:UpdateProgramPageImageResponse): Promise<IdResponse> {
  const baseUrl = process.env.REACT_APP_API_URL;
  const endpoint = `/api/v1/team/${teamGuid}/program/custom/${customProgramGuid}/custom-sheet`;
  const response = await axios.put(baseUrl + endpoint, imagePageUpdate,{ timeout: 60000, headers: { 'Authorization': `Bearer ${bearerToken}` } });
  return response.data as IdResponse;
}

export async function generateCustomProgramApi(bearerToken: string, teamGuid: string, customProgramGuid: string): Promise<FileDataAsString> {
  const baseUrl = process.env.REACT_APP_API_URL;
  const endpoint = `/api/v1/team/${teamGuid}/program/custom/${customProgramGuid}/generate`;
  const response = await axios.get(baseUrl + endpoint, { timeout: 60000, headers: { 'Authorization': `Bearer ${bearerToken}` } });
  return response.data as FileDataAsString;
}

export async function getCustomProgramManifestApi( teamGuid: string, customProgramGuid: string): Promise<FileDataAsString> {
  const baseUrl = process.env.REACT_APP_API_URL;
  const endpoint = `/api/v1/team/${teamGuid}/program/custom/${customProgramGuid}/manifest`;
  const response = await axios.get(baseUrl + endpoint, { timeout: 60000, headers: {  } });
  return response.data as FileDataAsString;
}

export async function viewRosterApi(bearerToken: string, teamGuid: string, orderBy: string): Promise<FileDataAsString> {
  const baseUrl = process.env.REACT_APP_API_URL;
  const endpoint = `/api/v1/team/${teamGuid}/roster/view?orderby=${orderBy}`;

  const headers: Record<string, string> = {};

  if (bearerToken) {
    headers['Authorization'] = `Bearer ${bearerToken}`;
  }

  const response = await axios.get(baseUrl + endpoint, { timeout: 60000, headers});
  return response.data as FileDataAsString;
}

export async function generateBinderLabelsApi(bearerToken: string, teamGuid: string): Promise<FileDataAsString> {
  const baseUrl = process.env.REACT_APP_API_URL;
  const endpoint = `/api/v1/team/${teamGuid}/binder-sheet/generate`;
  const response = await axios.get(baseUrl + endpoint, { timeout: 60000, headers: { 'Authorization': `Bearer ${bearerToken}` } });
  return response.data as FileDataAsString;
}

export async function generateGameSheetApi(bearerToken: string, teamGuid: string): Promise<FileDataAsString> {
  const baseUrl = process.env.REACT_APP_API_URL;
  const endpoint = `/api/v1/team/${teamGuid}/game-sheet/generate`;
  const response = await axios.get(baseUrl + endpoint, { timeout: 60000, headers: { 'Authorization': `Bearer ${bearerToken}` } });
  return response.data as FileDataAsString;
}

export async function generateSponsorSheetApi(bearerToken: string, teamGuid: string): Promise<FileDataAsString> {
  const baseUrl = process.env.REACT_APP_API_URL;
  const endpoint = `/api/v1/team/${teamGuid}/sponsor-sheet/generate`;
  const response = await axios.get(baseUrl + endpoint, { timeout: 60000, headers: { 'Authorization': `Bearer ${bearerToken}` } });
  return response.data as FileDataAsString;
}

export async function generateNameLabelsApi(bearerToken: string, teamGuid: string): Promise<FileDataAsString> {
  const baseUrl = process.env.REACT_APP_API_URL;
  const endpoint = `/api/v1/team/${teamGuid}/labels`;
  const response = await axios.get(baseUrl + endpoint, { timeout: 60000, headers: { 'Authorization': `Bearer ${bearerToken}` } });
  return response.data as FileDataAsString;
}

export async function getCardDataQRCardsApi(bearerToken: string, teamGuid: string): Promise<FileDataAsString> {
  const baseUrl = process.env.REACT_APP_API_URL;
  const endpoint = `/api/v1/team/${teamGuid}/card-data/qr-cards`;
  const response = await axios.get(baseUrl + endpoint, { timeout: 60000, headers: { 'Authorization': `Bearer ${bearerToken}` } });
  return response.data as FileDataAsString;
}

export async function generateTeamTradingCardsApi(bearerToken: string, teamGuid: string, layout: string): Promise<FileDataAsString> {
  const baseUrl = process.env.REACT_APP_API_URL;
  const endpoint = `/api/v1/team/${teamGuid}/trading-cards/${layout}/generate`;
  const response = await axios.get(baseUrl + endpoint, { timeout: 180000, headers: { 'Authorization': `Bearer ${bearerToken}` } });
  return response.data as FileDataAsString;
}

export async function viewTeamTradingCardsApi(bearerToken: string, teamGuid: string, layout: string): Promise<FileDataAsString> {
  const baseUrl = process.env.REACT_APP_API_URL;
  const endpoint = `/api/v1/team/${teamGuid}/trading-cards/${layout}/view`;
  const response = await axios.get(baseUrl + endpoint, { timeout: 180000, headers: { 'Authorization': `Bearer ${bearerToken}` } });
  return response.data as FileDataAsString;
}

//member profile and copy member

export async function getProfileZipsApi(bearerToken: string, orgSeasonGuid: string): Promise<FileDataAsString> {
  const baseUrl = process.env.REACT_APP_API_URL;
  const endpoint = `/api/v1/orgSeason/${orgSeasonGuid}/images/profile`;
  const response = await axios.get(baseUrl + endpoint, { timeout: 180000, headers: { 'Authorization': `Bearer ${bearerToken}` } });
  return response.data as FileDataAsString;
}

export async function getTeamProfileZipsApi(bearerToken: string, teamGuid: string): Promise<FileDataAsString> {
  const baseUrl = process.env.REACT_APP_API_URL;
  const endpoint = `/api/v1/team/${teamGuid}/images/profile`;
  const response = await axios.get(baseUrl + endpoint, { timeout: 180000, headers: { 'Authorization': `Bearer ${bearerToken}` } });
  return response.data as FileDataAsString;
}

export async function copyMemberDataApi(bearerToken: string, orgSeasonGuid: string, copyCommand: CopyMemberDataCommand): Promise<CopyMemberResult> {
  const baseUrl = process.env.REACT_APP_API_URL;
  const endpoint = `/api/v1/orgSeason/${orgSeasonGuid}/copy`;
  const response = await axios.post(baseUrl + endpoint, copyCommand, { timeout: 120000, headers: { 'Authorization': `Bearer ${bearerToken}` } });
  return response.data as CopyMemberResult;
}

//manage games

export async function addGameApi(bearerToken: string, teamGuid: string, addGameCommand:GameCommand): Promise<IdResponse> {
  const baseUrl = process.env.REACT_APP_API_URL;
  const endpoint = `/api/v1/team/${teamGuid}/game`;
  const response = await axios.post(baseUrl + endpoint,addGameCommand, { timeout: 60000, headers: { 'Authorization': `Bearer ${bearerToken}` } });
  return response.data as IdResponse;
}

export async function updateGameApi(bearerToken: string, teamGuid: string, gameGuid:string, addGameCommand:GameCommand): Promise<IdResponse> {
  const baseUrl = process.env.REACT_APP_API_URL;
  const endpoint = `/api/v1/team/${teamGuid}/game/${gameGuid}`;
  const response = await axios.put(baseUrl + endpoint,addGameCommand, { timeout: 60000, headers: { 'Authorization': `Bearer ${bearerToken}` } });
  return response.data as IdResponse;
}

export async function updateGameOpponentLogoApi(bearerToken: string, teamGuid: string, gameGuid:string, imageData: AddImageCommand): Promise<void> {
  const baseUrl = process.env.REACT_APP_API_URL;
  const endpoint = `/api/v1/team/${teamGuid}/game/${gameGuid}/opponent-logo`;
  await axios.put(baseUrl + endpoint, imageData, { timeout: 60000, headers: { 'Authorization': `Bearer ${bearerToken}` } });
}

export async function deleteGameApi(bearerToken: string, teamGuid: string, gameGuid:string): Promise<IdResponse> {
  const baseUrl = process.env.REACT_APP_API_URL;
  const endpoint = `/api/v1/team/${teamGuid}/game/${gameGuid}`;
  const response = await axios.delete(baseUrl + endpoint,{ timeout: 60000, headers: { 'Authorization': `Bearer ${bearerToken}` } });
  return response.data as IdResponse;
}








